/**
 * Вкладка «Обзор» кабинета хозяйства: блоки в порядке правил приоритета
 * (`priority.ts`). Первым — «Сейчас» (или «Итоги и подготовка» в
 * межсезонье), затем то, что требует внимания, — с объяснением, почему блок
 * наверху; потом ключевые показатели, земля, направления, карта, история;
 * последним — «Похожие хозяйства» по профилю метрик (ADR-0014).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { api, request, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { defineMessages } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { tr, numberLocale } from '../../../i18n/runtime';
import { Card, Grid, Stack } from '../../ui/Card';
import { KpiCard } from '../../ui/KpiCard';
import { DoneSplit } from '../../ui/DoneSplit';
import { areaShareLabel, areaSplit, haOf, hasArea, noArea } from '../../lib/areaWork';
import { Badge } from '../../ui/Badge';
import { InfoPopover } from '../../../components/InfoPopover';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { AgroMap } from '../../map/MapWidget';
import type { ColorBy } from '../../map/paint';
import { workStatusByContour, type WaterStateValue } from '../../map/layers';
import { useAsOfTimeline } from '../../map/useAsOfTimeline';
import { useTrackData } from '../../data/useTrackData';
import { FILES } from '../../data/loader';
import type { Crop } from '../../data/types';
import { dateShort, ha, m3Compact, num, pct, windowShort } from '../../lib/format';
import { toSituation } from '../../data-source/api/situations';
import { breakdownKinds, ProblemCard, type ProblemKind } from '../../astra/problems';
import {
  currentWorks,
  cropSeasonLayer,
  cycleName,
  directionsBySeason,
  landHeadline,
  metricNumber,
  quarantineByContour,
  waterByContour,
  workHref,
  workPhase,
  workTitle,
  WORK_PHASE_LABEL,
  doneSplit,
} from './model';
import { harvestYieldCHa, useFarmDeliveryRow } from './harvestYield';
import type { FarmBlock } from './priority';
import type { FarmCabinetData } from './useFarmCabinet';
import type { FarmTab } from './FarmCabinet';
import { SimilarFarms } from './SimilarFarms';
import { farmWaterCycles } from './FarmTabs';
import { NowCyclesSkeleton, OverviewSkeleton } from './CabinetSkeleton';
import { SkeletonKpi, SkeletonList, SkeletonMap } from '../../ui/Skeleton';
import { isLoading } from '../../ui/AsyncBlock';

const m = defineMessages({
  noData: { id: 'farm.overview.noData', defaultMessage: 'нет данных', description: 'Вместо числа в KPI и строках обзора хозяйства, когда сервер не вернул значение; честное «нет данных», а не 0; строчными' },
  noAnswer: { id: 'farm.overview.noAnswer', defaultMessage: 'сервер не ответил', description: 'Пояснение ошибки загрузки, когда у ошибки нет собственного текста; строчными' },
  asOf: { id: 'farm.overview.asOf', defaultMessage: 'на {date}', description: 'Подпись справа в шапке карточки обзора хозяйства; {date} — дата среза' },
  contourCount: { id: 'farm.overview.contourWord', defaultMessage: '{count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}', description: 'Число контуров с единицей («5 контуров»)' },
  splitFull: { id: 'farm.overview.split.full', defaultMessage: 'выполнено {done} · в процессе {progress} · нет отчёта {silent} из {total}', description: 'Три исхода работ на полях одной строкой; {done}/{progress}/{silent}/{total} — числа строкой' },
  splitReported: { id: 'farm.overview.split.reported', defaultMessage: 'отчитались {reported} из {total}', description: 'Исход работ на полях одной строкой, когда сервер не разделил «выполнено» и «в процессе»; числа строкой' },
  why: { id: 'farm.overview.why', defaultMessage: 'Почему здесь: {reasons}', description: 'Строка над содержимым блока обзора хозяйства: почему блок поднят наверх; {reasons} — готовые предложения через пробел' },
  allClear: { id: 'farm.overview.allClear', defaultMessage: 'Существенных сигналов нет на {date}: после срока, вода, карантин и расхождения — ноль.', description: 'Компактная зелёная строка «всё в порядке» на обзоре хозяйства; {date} — дата среза' },
  // Сейчас
  nowTitle: { id: 'farm.overview.now.title', defaultMessage: 'Сейчас', description: 'Заголовок карточки «Сейчас» на вкладке «Обзор» хозяйства (идущие агроциклы и текущие работы)' },
  nowFailTitle: { id: 'farm.overview.now.failTitle', defaultMessage: 'Текущее состояние не прочитано', description: 'Заголовок ошибки загрузки карточки «Сейчас»' },
  sigOverdueHa: { id: 'farm.overview.now.sig.overdueHa', defaultMessage: 'га земли с работой после срока · {works} работ', description: 'Чип «Проблемы прямо сейчас» после числа гектаров земли; {works} — число работ на полях после срока строкой' },
  sigOverdueNoArea: { id: 'farm.overview.now.sig.overdueNoArea', defaultMessage: 'работ после срока (площадь не указана)', description: 'Чип «Проблемы прямо сейчас» после числа; площади нет, считаем работы' },
  sigDueSoon: { id: 'farm.overview.now.sig.dueSoon', defaultMessage: 'работ, срок близко', description: 'Чип «Проблемы прямо сейчас» после числа: работы, у которых скоро закрывается окно' },
  sigWaterGap: { id: 'farm.overview.now.sig.waterGap', defaultMessage: 'поливов без подачи (поле × полив)', description: 'Чип «Проблемы прямо сейчас» после числа: пары «поле × полив» без поданной воды' },
  sigRed: { id: 'farm.overview.now.sig.red', defaultMessage: 'контуров в красном карантине', description: 'Чип «Проблемы прямо сейчас» после числа: контуры с красным карантинным статусом' },
  sigYellow: { id: 'farm.overview.now.sig.yellow', defaultMessage: 'контуров под наблюдением', description: 'Чип «Проблемы прямо сейчас» после числа: контуры с жёлтым карантинным статусом' },
  sigConflicts: { id: 'farm.overview.now.sig.conflicts', defaultMessage: 'расхождений', description: 'Чип «Проблемы прямо сейчас» после числа: расхождения отчёта и проверки' },
  sigChip: { id: 'farm.overview.now.sig.chip', defaultMessage: '{value} · {label}', description: 'Чип «Проблемы прямо сейчас»; {value} — число, {label} — что посчитано' },
  sigTitle: { id: 'farm.overview.now.sig.title', defaultMessage: 'Проблемы прямо сейчас', description: 'Подзаголовок списка чипов проблем в карточке «Сейчас»' },
  sigNone: { id: 'farm.overview.now.sig.none', defaultMessage: 'На {date} сигналов нет.', description: 'Строка в блоке «Проблемы прямо сейчас», когда все сигналы нулевые; {date} — дата среза' },
  stageLine: { id: 'farm.overview.now.stageLine', defaultMessage: 'Этап: {stage} · окно агроцикла {window}', description: 'Строка под названием идущего агроцикла; {stage} — название этапа (из данных), {window} — даты окна' },
  stageNone: { id: 'farm.overview.now.stageNone', defaultMessage: 'этап не назван · окно агроцикла {window}', description: 'Строка под названием идущего агроцикла, когда у него нет названия этапа; {window} — даты окна' },
  underCycle: { id: 'farm.overview.now.underCycle', defaultMessage: 'Под агроциклом {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}', description: 'Строка под названием идущего агроцикла; {contours} — число контуров' },
  underCycleHa: { id: 'farm.overview.now.underCycleHa', defaultMessage: 'Под агроциклом {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} · {ha} га', description: 'Строка под названием идущего агроцикла; {contours} — число контуров, {ha} — площадь земли строкой (га земли)' },
  doneDenominator: { id: 'farm.overview.now.doneDenominator', defaultMessage: 'га работ хозяйства в агроцикле', description: 'Знаменатель под полосой выполнения (по площади): «Знаменатель — 46 738 га работ хозяйства в агроцикле»' },
  doneDenominatorCount: { id: 'farm.overview.now.doneDenominatorCount', defaultMessage: '{count, plural, one {работа на поле хозяйства в агроцикле} few {работы на полях хозяйства в агроцикле} many {работ на полях хозяйства в агроцикле} other {работы на полях хозяйства в агроцикле}}', description: 'Знаменатель под полосой выполнения (по числу); слово без числа, согласуется с {count}' },
  windowNote: { id: 'farm.overview.now.windowNote', defaultMessage: 'часть окон ещё идёт — нет отчёта не равно «не сделано».', description: 'Пояснение под полосой выполнения по площади; строчными (встаёт как предложение)' },
  windowNoteNoArea: { id: 'farm.overview.now.windowNoteNoArea', defaultMessage: '{noArea} — полоса по числу работ; часть окон ещё идёт.', description: 'Пояснение под полосой выполнения, когда площади нет; {noArea} — «площадь не указана»' },
  byCount: { id: 'farm.overview.now.byCount', defaultMessage: 'По числу работ: выполнено {done} из {total}', description: 'Мелкая строка под полосой выполнения по площади; {done}/{total} — числа строкой' },
  currentWorks: { id: 'farm.overview.now.currentWorks', defaultMessage: 'Текущие работы', description: 'Подзаголовок списка работ, у которых окно открыто на дату среза' },
  currentNone: { id: 'farm.overview.now.currentNone', defaultMessage: 'На {date} окно ни одной работы агроцикла не открыто.', description: 'Пустое состояние списка текущих работ; {date} — дата среза' },
  workMeta: { id: 'farm.overview.now.workMeta', defaultMessage: 'окно {window} · {rest} · {phase}', description: 'Мелкая строка под названием текущей работы; {window} — даты окна, {rest} — выполнение, {phase} — состояние работы строчными' },
  workMetaArea: { id: 'farm.overview.now.workMetaArea', defaultMessage: 'выполнено {done} из {total} га работ · по числу работ: {split}', description: 'Часть мелкой строки под работой: выполненные гектары работ из общих и итог по числу; {done}/{total} — числа строкой, {split} — «выполнено 13 · в процессе 13 · нет отчёта 104 из 130»' },
  soonTitle: { id: 'farm.overview.now.soonTitle', defaultMessage: 'Срочно в ближайшие дни', description: 'Подзаголовок списка работ, срок которых вот-вот закроется' },
  soonDeadline: { id: 'farm.overview.now.soonDeadline', defaultMessage: 'срок {date}', description: 'Мелкая строка под срочной работой; {date} — дата закрытия окна' },
  soonDeadlineDays: { id: 'farm.overview.now.soonDeadlineDays', defaultMessage: 'срок {date} (через {days, plural, one {# день} few {# дня} many {# дней} other {# дня}}) · {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} без отчёта', description: 'Мелкая строка под срочной работой; {date} — дата закрытия окна, {days} — дней осталось, {contours} — контуров без отчёта' },
  soonNoDays: { id: 'farm.overview.now.soonNoDays', defaultMessage: 'срок {date} · {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} без отчёта', description: 'Мелкая строка под срочной работой, когда число дней неизвестно; {date} — дата, {contours} — контуров без отчёта' },
  // Межсезонье
  offTitle: { id: 'farm.overview.off.title', defaultMessage: 'Межсезонье: итоги и подготовка', description: 'Заголовок карточки обзора хозяйства в межсезонье: итоги прошлых агроциклов и подготовка' },
  offNoneTitle: { id: 'farm.overview.off.noneTitle', defaultMessage: 'Завершённых агроциклов нет', description: 'Заголовок пустого состояния блока итогов межсезонья' },
  offNoneReason: { id: 'farm.overview.off.noneReason', defaultMessage: 'у хозяйства в срезе нет ни одного закрытого агроцикла', description: 'Пояснение пустого состояния блока итогов межсезонья' },
  offClosedMeta: { id: 'farm.overview.off.closedMeta', defaultMessage: '{split} · после срока {overdue}', description: 'Мелкая строка под завершённым агроциклом; {split} — итог по работам на полях, {overdue} — число после срока строкой' },
  offPrep: { id: 'farm.overview.off.prep', defaultMessage: 'Подготовка: {list}.', description: 'Строка в блоке межсезонья: ближайшие запланированные агроциклы; {list} — через точку с запятой' },
  offPrepItem: { id: 'farm.overview.off.prepItem', defaultMessage: '{cycle} — окно с {date}', description: 'Один запланированный агроцикл в строке подготовки; {cycle} — «Хлопок 2026», {date} — дата начала окна' },
  offPrepNone: { id: 'farm.overview.off.prepNone', defaultMessage: 'запланированных агроциклов в срезе нет', description: 'Значение строки «Подготовка», когда запланированных агроциклов нет; строчными' },
  // Ключевые показатели
  kpiTitle: { id: 'farm.overview.kpi.title', defaultMessage: 'Ключевые показатели', description: 'Заголовок карточки ключевых показателей на обзоре хозяйства' },
  kpiFailTitle: { id: 'farm.overview.kpi.failTitle', defaultMessage: 'Показатели не прочитаны', description: 'Заголовок ошибки загрузки ключевых показателей' },
  kpiDone: { id: 'farm.overview.kpi.done', defaultMessage: 'Выполнено', description: 'Подпись KPI-карточки: доля выполненных работ хозяйства; коротко' },
  kpiDoneNote: { id: 'farm.overview.kpi.doneNote', defaultMessage: '{done} из {work} га работ; по числу работ {share}', description: 'Нижняя строка KPI «Выполнено» (по площади); {done}/{work} — гектары работ строкой, {share} — процент по числу работ' },
  kpiDoneNoteCount: { id: 'farm.overview.kpi.doneNoteCount', defaultMessage: '{noArea}: по числу работ — {done} из {total}; в процессе {progress}', description: 'Нижняя строка KPI «Выполнено», когда площади нет; {noArea} — «площадь не указана», остальное — числа строкой' },
  kpiOverdue: { id: 'farm.overview.kpi.overdue', defaultMessage: 'После срока', description: 'Подпись KPI-карточки: земля или работы после срока; коротко' },
  kpiLandUnit: { id: 'farm.overview.kpi.landUnit', defaultMessage: 'га земли', description: 'Единица KPI «После срока»: гектары уникальной земли (не сумма по работам); коротко' },
  kpiOverdueNote: { id: 'farm.overview.kpi.overdueNote', defaultMessage: '{share} земли агроцикла · {works} работ · {ha} га работ', description: 'Нижняя строка KPI «После срока» (по площади); {share} — процент земли строкой, {works} — число работ строкой, {ha} — гектары работ строкой' },
  kpiWorksUnit: { id: 'farm.overview.kpi.worksUnit', defaultMessage: 'работ', description: 'Единица KPI «После срока», когда площади нет: число работ; коротко' },
  kpiOverdueNoteCount: { id: 'farm.overview.kpi.overdueNoteCount', defaultMessage: '{noArea}; {share} работ с закрытым окном', description: 'Нижняя строка KPI «После срока» без площади; {noArea} — «площадь не указана», {share} — процент работ с закрытым окном строкой' },
  kpiHarvest: { id: 'farm.overview.kpi.harvest', defaultMessage: 'Сдача против плана', description: 'Подпись KPI-карточки: доля сдачи от плана; коротко' },
  kpiHarvestNote: { id: 'farm.overview.kpi.harvestNote', defaultMessage: 'сдано {delivered} из {plan} т', description: 'Нижняя строка KPI «Сдача против плана»; {delivered}/{plan} — тонны строкой' },
  kpiWater: { id: 'farm.overview.kpi.water', defaultMessage: 'Подано воды', description: 'Подпись KPI-карточки: доля поданной воды от запрошенной; коротко' },
  kpiWaterNote: { id: 'farm.overview.kpi.waterNote', defaultMessage: '{delivered} из {requested} тыс. м³ запрошенного', description: 'Нижняя строка KPI «Подано воды»; {delivered}/{requested} — тысячи кубометров строкой' },
  kpiQuarantine: { id: 'farm.overview.kpi.quarantine', defaultMessage: 'Карантин', description: 'Подпись KPI-карточки: контуры в карантине; коротко' },
  kpiContoursUnit: { id: 'farm.overview.kpi.contoursUnit', defaultMessage: 'контуров', description: 'Единица KPI «Карантин»: контуры в жёлтом или красном статусе; коротко' },
  kpiQuarantineNote: { id: 'farm.overview.kpi.quarantineNote', defaultMessage: 'предписаний открыто {open}, после срока {overdue}', description: 'Нижняя строка KPI «Карантин»; {open}/{overdue} — числа строкой' },
  kpiQuarantineLoading: { id: 'farm.overview.kpi.quarantineLoading', defaultMessage: 'читаем карантин', description: 'Нижняя строка KPI «Карантин», пока данные грузятся' },
  openCycles: { id: 'farm.overview.kpi.openCycles', defaultMessage: 'Открыть вкладку «Агроциклы и работы»', description: 'Подсказка нажимаемой KPI-карточки: переход на вкладку «Агроциклы и работы»' },
  openHarvest: { id: 'farm.overview.kpi.openHarvest', defaultMessage: 'Открыть вкладку «Сдача»', description: 'Подсказка нажимаемой KPI-карточки: переход на вкладку «Сдача»' },
  openWater: { id: 'farm.overview.kpi.openWater', defaultMessage: 'Открыть вкладку «Вода»', description: 'Подсказка нажимаемой KPI-карточки: переход на вкладку «Вода»' },
  openQuarantine: { id: 'farm.overview.kpi.openQuarantine', defaultMessage: 'Открыть вкладку «Карантин»', description: 'Подсказка нажимаемой KPI-карточки: переход на вкладку «Карантин»' },
  openContours: { id: 'farm.overview.kpi.openContours', defaultMessage: 'Открыть вкладку «Контуры»', description: 'Подсказка нажимаемой KPI-карточки: переход на вкладку «Контуры»' },
  noHarvestPlan: { id: 'farm.overview.kpi.noHarvestPlan', defaultMessage: 'Плана сдачи у хозяйства в срезе нет — показатель сдачи не выводится.', description: 'Пояснение под ключевыми показателями, когда у хозяйства нет плана сдачи' },
  // Сдача
  harvestTitle: { id: 'farm.overview.harvest.title', defaultMessage: 'Сдача урожая', description: 'Заголовок карточки сдачи урожая на обзоре хозяйства' },
  harvestMore: { id: 'farm.overview.harvest.more', defaultMessage: 'Подробно', description: 'Кнопка-ссылка в шапке карточки сдачи: открыть вкладку «Сдача»; коротко' },
  harvestDelivered: { id: 'farm.overview.harvest.delivered', defaultMessage: 'Сдано', description: 'Подпись KPI-карточки: сданная масса, т; коротко' },
  harvestDeliveredNote: { id: 'farm.overview.harvest.deliveredNote', defaultMessage: 'план {plan} т', description: 'Нижняя строка KPI «Сдано»; {plan} — плановая масса, т, строкой' },
  harvestCompletion: { id: 'farm.overview.harvest.completion', defaultMessage: 'Выполнение плана', description: 'Подпись KPI-карточки: доля сдачи от плана' },
  harvestCompletionNote: { id: 'farm.overview.harvest.completionNote', defaultMessage: 'сдано от контрактной массы', description: 'Нижняя строка KPI «Выполнение плана»' },
  harvestYield: { id: 'farm.overview.harvest.yield', defaultMessage: 'Урожайность', description: 'Подпись KPI-карточки: урожайность, ц/га' },
  harvestYieldUnit: { id: 'farm.overview.harvest.yieldUnit', defaultMessage: 'ц/га', description: 'Единица KPI «Урожайность»: центнеры с гектара; очень коротко' },
  harvestYieldNote: { id: 'farm.overview.harvest.yieldNote', defaultMessage: 'производная: сданное / площадь с отчётом о сборе', description: 'Нижняя строка KPI «Урожайность»: как посчитано' },
  // Земля
  landTitle: { id: 'farm.overview.land.title', defaultMessage: 'Земля, поля и агроциклы', description: 'Заголовок карточки ключевых фактов хозяйства на обзоре: земля, контуры (поля), агроциклы, культуры по годам' },
  landArableSplit: { id: 'farm.overview.land.arableSplit', defaultMessage: 'орошаемая {irrigated} · богара {rainfed}', description: 'Нижняя строка KPI «Пашня»: орошаемая и неорошаемая (богарная) пашня; {irrigated}/{rainfed} — гектары с единицей или «нет данных»' },
  landCycles: { id: 'farm.overview.land.cycles', defaultMessage: 'Агроциклов', description: 'Подпись KPI-карточки: число агроциклов хозяйства в срезе (где у него есть работы); коротко' },
  landCyclesActive: { id: 'farm.overview.land.cyclesActive', defaultMessage: 'идёт: {names} · завершено {closed}', description: 'Нижняя строка KPI «Агроциклов»; {names} — идущие агроциклы через запятую («Хлопок 2026»), {closed} — число завершённых' },
  landCyclesNone: { id: 'farm.overview.land.cyclesNone', defaultMessage: 'ни один не идёт · завершено {closed}', description: 'Нижняя строка KPI «Агроциклов» в межсезонье; {closed} — число завершённых' },
  landAside: { id: 'farm.overview.land.aside', defaultMessage: '{contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} по реестру', description: 'Подпись справа в шапке карточки земли; {contours} — число контуров хозяйства по реестру' },
  landFailTitle: { id: 'farm.overview.land.failTitle', defaultMessage: 'Земля не прочитана', description: 'Заголовок ошибки загрузки земли хозяйства' },
  landLoading: { id: 'farm.overview.land.loading', defaultMessage: 'землю хозяйства', description: 'Что грузится (винительный падеж, читает диктор «Загружаю …»): земля хозяйства' },
  landTotal: { id: 'farm.overview.land.total', defaultMessage: 'Земля всего', description: 'Подпись KPI-карточки: вся земля хозяйства, га; коротко' },
  landTotalNote: { id: 'farm.overview.land.totalNote', defaultMessage: 'все землепользования хозяйства', description: 'Нижняя строка KPI «Земля всего»' },
  landArable: { id: 'farm.overview.land.arable', defaultMessage: 'Пашня', description: 'Подпись KPI-карточки: пахотные земли, га; коротко' },
  landContours: { id: 'farm.overview.land.contours', defaultMessage: 'Контуров', description: 'Подпись KPI-карточки: число контуров хозяйства; коротко' },
  landContoursNote: { id: 'farm.overview.land.contoursNote', defaultMessage: 'в таблице контуров — площадь, культуры по годам', description: 'Нижняя строка KPI «Контуров»' },
  haValue: { id: 'farm.overview.haValue', defaultMessage: '{ha} га', description: 'Гектары с единицей; {ha} — число строкой' },
  // Направления
  dirTitle: { id: 'farm.overview.dir.title', defaultMessage: 'Культуры по годам агроцикла', description: 'Подзаголовок внутри карточки «Земля, поля и агроциклы»: культуры хозяйства по годам агроцикла (направления)' },
  dirAside: { id: 'farm.overview.dir.aside', defaultMessage: 'по реестру: {list}', description: 'Подпись справа в шапке карточки направлений; {list} — направления из реестра через запятую' },
  dirLoading: { id: 'farm.overview.dir.loading', defaultMessage: 'культуры по сезонам', description: 'Что грузится (винительный падеж, читает диктор «Загружаю …»): культуры по сезонам' },
  dirNoneReason: { id: 'farm.overview.dir.noneReason', defaultMessage: 'занятостей культур на контурах хозяйства в срезе нет — это пробел данных, а не пар', description: 'Пояснение пустого состояния карточки направлений: данных о посевах нет (не паровое поле)' },
  dirYear: { id: 'farm.overview.dir.year', defaultMessage: 'Год агроцикла {year}', description: 'Заголовок колонки направлений: год агроцикла; {year} — год' },
  dirItem: { id: 'farm.overview.dir.item', defaultMessage: '{ha} · {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}', description: 'Строка культуры в направлениях; {ha} — площадь с единицей или «нет данных», {contours} — число контуров' },
  dirMore: { id: 'farm.overview.dir.more', defaultMessage: 'и ещё {count}', description: 'Строка в направлениях: сколько культур года не показано; {count} — число' },
  // Карта
  mapTitle: { id: 'farm.overview.map.title', defaultMessage: 'Карта хозяйства', description: 'Заголовок карточки карты хозяйства на обзоре' },
  mapInfo: { id: 'farm.overview.map.info', defaultMessage: 'Дата — ползунком под картой: статус работ, вода и карантин пересчитываются на выбранный день.', description: 'Текст всплывающей подсказки «i» карточки карты хозяйства' },
  mapSeason: { id: 'farm.overview.map.season', defaultMessage: 'Сезон культур', description: 'Подпись выпадающего списка над картой: за какой сезон показывать культуры' },
  mapRegistry: { id: 'farm.overview.map.registry', defaultMessage: 'текущее размещение (реестр)', description: 'Пункт списка «Сезон культур»: показывать текущее размещение культур по реестру' },
  mapYear: { id: 'farm.overview.map.year', defaultMessage: 'год агроцикла {year}', description: 'Пункт списка «Сезон культур»: год агроцикла; {year} — год' },
  mapLoading: { id: 'farm.overview.map.loading', defaultMessage: 'карту хозяйства', description: 'Что грузится (винительный падеж, читает диктор «Загружаю …»): карта хозяйства' },
  kpiWaterFileNote: { id: 'farm.overview.kpi.waterFileNote', defaultMessage: 'по файлу ирригации · полито {land} га ({done} земли плана) · без подачи {noDelivery}', description: 'Нижняя строка KPI «Подано воды» (вода по файлу ирригации); {land} — гектары, {done} — доля земли плана с поливом (проценты), {noDelivery} — число пар «поле × полив» без подачи' },
  mapNoWater: { id: 'farm.overview.map.noWater', defaultMessage: 'у хозяйства нет данных о поливе в срезе', description: 'Пояснение на карте, когда слой «Вода» недоступен' },
  mapNoStatus: { id: 'farm.overview.map.noStatus', defaultMessage: 'статус работ по контурам не собран', description: 'Пояснение на карте, когда слой «Статус работ» недоступен' },
  mapCaptionStatus: { id: 'farm.overview.map.captionStatus', defaultMessage: 'Статус работ «{cycle}» на дату', description: 'Подпись под картой хозяйства при слое «Статус работ»; {cycle} — «Хлопок 2026»' },
  mapCaptionOnly: { id: 'farm.overview.map.captionOnly', defaultMessage: 'Только земля этого хозяйства', description: 'Подпись под картой хозяйства: на карте только контуры этого хозяйства' },
  // История
  histTitle: { id: 'farm.overview.hist.title', defaultMessage: 'Прошлые агроциклы', description: 'Заголовок карточки истории агроциклов на обзоре хозяйства' },
  histAll: { id: 'farm.overview.hist.all', defaultMessage: 'Все агроциклы', description: 'Кнопка-ссылка в шапке карточки истории: открыть вкладку «Агроциклы и работы»; коротко' },
  histNone: { id: 'farm.overview.hist.none', defaultMessage: 'Завершённых агроциклов у хозяйства в срезе нет.', description: 'Пустое состояние карточки истории агроциклов' },
  histMeta: { id: 'farm.overview.hist.meta', defaultMessage: '{contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} · {split} · после срока {overdue}', description: 'Мелкая строка под прошлым агроциклом; {contours} — число контуров, {split} — итог по работам на полях, {overdue} — число после срока строкой' },
  // Поручения
  asgTitle: { id: 'farm.overview.asg.title', defaultMessage: 'Поручения по хозяйству', description: 'Заголовок карточки поручений на обзоре хозяйства' },
  asgCount: { id: 'farm.overview.asg.count', defaultMessage: '{count, plural, one {# поручение} few {# поручения} many {# поручений} other {# поручения}}', description: 'Число поручений — подпись справа в шапке карточки' },
  asgLoading: { id: 'farm.overview.asg.loading', defaultMessage: 'поручения', description: 'Что грузится (винительный падеж, читает диктор «Загружаю …»): поручения' },
  asgFailTitle: { id: 'farm.overview.asg.failTitle', defaultMessage: 'Поручения не прочитаны', description: 'Заголовок ошибки загрузки поручений' },
  asgFailReason: { id: 'farm.overview.asg.failReason', defaultMessage: 'журнал поручений доступен после входа с правом на ситуации', description: 'Пояснение ошибки загрузки поручений: нужен вход с правом' },
  asgNone: { id: 'farm.overview.asg.none', defaultMessage: 'По хозяйству поручений не ставили. Поручить уточнение можно из карточки проблемы выше.', description: 'Пустое состояние карточки поручений' },
  asgMeta: { id: 'farm.overview.asg.meta', defaultMessage: '{status} · {assignee} · {due}', description: 'Мелкая строка под поручением; {status} — статус, {assignee} — исполнитель, {due} — срок' },
  asgDue: { id: 'farm.overview.asg.due', defaultMessage: 'срок {date}', description: 'Срок поручения в мелкой строке; {date} — дата' },
  asgDueNone: { id: 'farm.overview.asg.dueNone', defaultMessage: 'срок не назначен', description: 'Срок поручения в мелкой строке, когда срок не назначен' },
  asgOpen: { id: 'farm.overview.asg.status.open', defaultMessage: 'открыто', description: 'Статус поручения: открыто (ещё не взято); строчными' },
  asgInProgress: { id: 'farm.overview.asg.status.inProgress', defaultMessage: 'в работе', description: 'Статус поручения: исполнитель работает; строчными' },
  asgDone: { id: 'farm.overview.asg.status.done', defaultMessage: 'исполнено', description: 'Статус поручения: исполнено, ждёт проверки; строчными' },
  asgChecked: { id: 'farm.overview.asg.status.checked', defaultMessage: 'закрыто', description: 'Статус поручения: проверено и закрыто; строчными' },
});

/** Дробное с одним знаком, как в KPI «Выполнено»: «78,4». */
function oneDecimal(x: number): string {
  return new Intl.NumberFormat(numberLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(x);
}

interface Props {
  data: FarmCabinetData;
  farmId: number;
  farmName: string;
  openTab: (tab: FarmTab) => void;
}

const ALERT_KIND: Partial<Record<FarmBlock, ProblemKind>> = {
  'alert-quarantine': 'quarantine',
  'alert-deadlines': 'deadlines',
  'alert-water': 'water',
  'alert-conflicts': 'conflicts',
};

/** «выполнено 13 · в процессе 13 · нет отчёта 104 из 130» — три исхода (журнал №290). */
function splitText(o: { total: number; reported: number; done?: number | null; in_progress?: number | null }): string {
  const d = doneSplit(o);
  if (d.done === null) return tr(m.splitReported, { reported: num(d.reported), total: num(d.total) });
  return tr(m.splitFull, { done: num(d.done), progress: num(d.inProgress ?? 0), silent: num(d.silent), total: num(d.total) });
}

/** Почему блок здесь — строка над содержанием (правила приоритета). */
function Why({ reasons }: { reasons: string[] }) {
  const t = useT();
  return <p className="cab-why">{t(m.why, { reasons: reasons.join(' ') })}</p>;
}

export function FarmOverview({ data, farmId, farmName, openTab }: Props) {
  const { arrangement, factsReady } = data;
  const t = useT();
  const blocks = arrangement.shown;
  const render = (block: FarmBlock, reasons: string[]): ReactNode => {
    switch (block) {
      case 'now':
        return <NowBlock key={block} data={data} farmId={farmId} openTab={openTab} />;
      case 'season-results':
        return <SeasonResults key={block} data={data} reasons={reasons} openTab={openTab} />;
      case 'all-clear':
        return (
          <div key={block} data-guide="all-clear" className="cab-allclear" role="status">
            <Icon icon={CheckCircle2} size={18} />
            <span>{t(m.allClear, { date: dateShort(data.asOf) })}</span>
          </div>
        );
      case 'indicators':
        return <Indicators key={block} data={data} openTab={openTab} />;
      case 'harvest':
        return <HarvestSummary key={block} data={data} farmId={farmId} reasons={reasons} openTab={openTab} />;
      case 'land':
        return <LandBlock key={block} data={data} openTab={openTab} />;
      case 'map':
        return <FarmMap key={block} data={data} farmId={farmId} />;
      case 'assignments':
        return <AssignmentsBlock key={block} data={data} farmId={farmId} />;
      case 'history':
        return <HistoryBlock key={block} data={data} reasons={reasons} openTab={openTab} />;
      default: {
        const kind = ALERT_KIND[block];
        if (!kind) return null;
        return <AlertBlock key={block} block={block} kind={kind} reasons={reasons} data={data} farmId={farmId} farmName={farmName} />;
      }
    }
  };
  // Порядок блоков зависит от фактов хозяйства: пока их собираем, на экране
  // раскладка вкладки целиком, а не «пустой» набор блоков.
  if (!factsReady) return <OverviewSkeleton nowCount={data.active.length || 1} />;
  return (
    <Stack>
      {blocks.map((b) => render(b.block, b.reasons))}
      {/* Похожие хозяйства (ADR-0014) — справка, а не сигнал: всегда последним,
          вне правил приоритета; области хозяйства не рисуется. */}
      <div data-guide="similar"><SimilarFarms data={data} farmId={farmId} /></div>
    </Stack>
  );
}

/* ------------------------------------------------------------- Сейчас */

function NowBlock({ data, farmId, openTab }: { data: FarmCabinetData; farmId: number; openTab: (t: FarmTab) => void }) {
  const { current, asOf, facts, ctx } = data;
  const t = useT();
  const noData = t(m.noData);
  const rows = current.data ?? [];
  // Чип «после срока» — ЗЕМЛЯ, где есть работа после срока (каждый гектар
  // один раз, журнал №690; метрика area.overdue_ha основного цикла), число
  // работ рядом; без площади — прежнее число работ.
  const overdueHa = metricNumber(data.metricById, 'area.overdue_ha');
  const signals: { label: string; value: number | null; tab: FarmTab; tone: 'warn' | 'critical' | null }[] = [
    overdueHa !== null
      ? { label: t(m.sigOverdueHa, { works: num(facts.overdue) }), value: Math.round(overdueHa), tab: 'cycles', tone: facts.overdue ? 'warn' : null }
      : { label: t(m.sigOverdueNoArea), value: facts.overdue, tab: 'cycles', tone: facts.overdue ? 'warn' : null },
    { label: t(m.sigDueSoon), value: facts.dueSoon, tab: 'cycles', tone: null },
    { label: t(m.sigWaterGap), value: facts.waterGapContours, tab: 'water', tone: facts.waterGapContours ? 'warn' : null },
    { label: t(m.sigRed), value: facts.redContours, tab: 'quarantine', tone: facts.redContours ? 'critical' : null },
    { label: t(m.sigYellow), value: facts.yellowContours, tab: 'quarantine', tone: facts.yellowContours ? 'warn' : null },
    { label: t(m.sigConflicts), value: facts.conflicts, tab: 'cycles', tone: facts.conflicts ? 'warn' : null },
  ];
  return (
    <div data-guide="now">
      <Card size="lg" title={t(m.nowTitle)} aside={t(m.asOf, { date: dateShort(asOf) })}>
        {data.currentLoading ? (
          <NowCyclesSkeleton count={data.active.length || 1} />
        ) : current.state === 'error' ? (
          <EmptyState compact title={t(m.nowFailTitle)} reason={current.error?.message ?? t(m.noAnswer)} />
        ) : (
          <div className="cab-now">
            {rows.map(({ cycle, picture, breakdown }) => {
              const works = picture.summary?.works ?? [];
              const now = currentWorks(works, asOf).slice(0, 3);
              const soon = (breakdown?.deadlines?.due_soon_works ?? []).slice(0, 3);
              return (
                <div className="cab-now__cycle" key={cycle.cycle}>
                  <div className="cab-now__head">
                    <span className="cab-now__name">{cycleName(cycle)}</span>
                    <span className="cab-now__meta">
                      {cycle.stage ? t(m.stageLine, { stage: cycle.stage.name, window: windowShort(cycle.window_from, cycle.window_to) }) : t(m.stageNone, { window: windowShort(cycle.window_from, cycle.window_to) })}
                    </span>
                    <span className="cab-now__meta">
                      {cycle.area.total_microha !== null
                        ? t(m.underCycleHa, { contours: cycle.contours.total, ha: ha(cycle.area.total_microha / 1_000_000) })
                        : t(m.underCycle, { contours: cycle.contours.total })}
                    </span>
                    {areaSplit(cycle.area_work) ? (
                      <>
                        <DoneSplit size="sm" counts={areaSplit(cycle.area_work)!} denominator={m.doneDenominator} windowNote={t(m.windowNote)} />
                        <span className="cab-now__meta">{t(m.byCount, { done: num(doneSplit(cycle.obligations).done), total: num(cycle.obligations.total) })}</span>
                      </>
                    ) : (
                      <DoneSplit size="sm" counts={{ total: cycle.obligations.total, reported: cycle.obligations.reported, inProgress: doneSplit(cycle.obligations).inProgress }} denominator={m.doneDenominatorCount} windowNote={t(m.windowNoteNoArea, { noArea: noArea() })} />
                    )}
                  </div>
                  <div>
                    <strong>{t(m.currentWorks)}</strong>
                    {now.length === 0 ? (
                      <p className="cab-why">{t(m.currentNone, { date: dateShort(asOf) })}</p>
                    ) : (
                      <ul className="cab-list">
                        {now.map((w) => (
                          <li className="cab-list__row" key={w.campaign_id}>
                            <Link to={workHref(cycle.cycle, w.campaign_id, farmId, ctx.linkSearch)}>{workTitle(w.name)}</Link>
                            <span className="cab-list__meta">
                              {t(m.workMeta, {
                                window: windowShort(w.window_from, w.window_to),
                                rest: hasArea(w.area_work)
                                  ? t(m.workMetaArea, { done: num(haOf(w.area_work!.done_microha)), total: num(haOf(w.area_work!.total_microha)), split: splitText(w.totals.obligations) })
                                  : splitText(w.totals.obligations),
                                phase: WORK_PHASE_LABEL[workPhase(w, asOf)],
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {soon.length > 0 ? (
                      <>
                        <strong>{t(m.soonTitle)}</strong>
                        <ul className="cab-list">
                          {soon.map((w) => (
                            <li className="cab-list__row" key={w.work_def_id}>
                              <span>{workTitle(w.name)}</span>
                              <span className="cab-list__meta">
                                {w.days_left !== null && w.days_left !== undefined
                                  ? t(m.soonDeadlineDays, { date: dateShort(w.window_to), days: w.days_left, contours: w.contours_total })
                                  : t(m.soonNoDays, { date: dateShort(w.window_to), contours: w.contours_total })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
            <div>
              <strong>{t(m.sigTitle)}</strong>
              {/* Нулевые чипы («0 · красный карантин») не рисуем: ноль — не проблема.
                  Не названное сервером (null) остаётся «нет данных». */}
              <div className="cab-signals">
                {signals.every((s) => s.value === 0) ? <span className="cab-why">{t(m.sigNone, { date: dateShort(asOf) })}</span> : null}
                {signals.filter((s) => s.value !== 0).map((s) => (
                  <button key={s.label} type="button" className={`cab-signal${s.tone ? ` cab-signal--${s.tone}` : ''}`} onClick={() => openTab(s.tab)}>
                    {t(m.sigChip, { value: s.value === null ? noData : num(s.value), label: s.label })}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* --------------------------------------------- межсезонье: итоги */

function SeasonResults({ data, reasons, openTab }: { data: FarmCabinetData; reasons: string[]; openTab: (t: FarmTab) => void }) {
  const t = useT();
  const noData = t(m.noData);
  const closed = data.items.filter((c) => c.status === 'closed').slice(0, 2);
  const planned = data.items.filter((c) => c.status === 'planned');
  return (
    <div data-guide="season-results">
      <Card size="lg" title={t(m.offTitle)} aside={t(m.asOf, { date: dateShort(data.asOf) })}>
        <Why reasons={reasons} />
        {closed.length === 0 ? (
          <EmptyState compact title={t(m.offNoneTitle)} reason={t(m.offNoneReason)} />
        ) : (
          <ul className="cab-list">
            {closed.map((c) => (
              <li className="cab-list__row" key={c.cycle}>
                <button type="button" className="cab-signal" onClick={() => openTab('cycles')}>{cycleName(c)}</button>
                <span className="cab-list__meta">
                  {t(m.offClosedMeta, { split: splitText(c.obligations), overdue: c.overdue.value === null ? noData : num(c.overdue.value) })}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="cab-why">
          {t(m.offPrep, { list: planned.length ? planned.map((c) => t(m.offPrepItem, { cycle: cycleName(c), date: dateShort(c.window_from) })).join('; ') : t(m.offPrepNone) })}
        </p>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------- проблемы */

function AlertBlock({ block, kind, reasons, data, farmId, farmName }: { block: FarmBlock; kind: ProblemKind; reasons: string[]; data: FarmCabinetData; farmId: number; farmName: string }) {
  // Разбор — по тому идущему циклу, где сигнал этого типа есть (у хозяйства
  // может идти хлопок и пшеница; расхождения могут быть только в одном).
  const withSignal = (data.current.data ?? []).find((c) => breakdownKinds(c.breakdown).includes(kind));
  const cycle = withSignal?.cycle.cycle ?? data.primary?.cycle ?? data.active[0]?.cycle ?? null;
  const situation = useMemo(() => {
    const row = data.situations?.items.find((r) => r.actor.id === farmId);
    return row ? toSituation(row) : null;
  }, [data.situations, farmId]);
  if (!cycle) return null;
  const body = (
    <>
      <Why reasons={reasons} />
      <ProblemCard kind={kind} farmId={farmId} farmName={farmName} situation={situation} scope={{ params: data.ctx.params, cycle, asOf: data.asOf, linkSearch: data.ctx.linkSearch }} />
    </>
  );
  // Метки блоков — литералами: каталог экранов сверяет разметку по тексту.
  switch (block) {
    case 'alert-quarantine':
      return <div data-guide="attention-quarantine">{body}</div>;
    case 'alert-deadlines':
      return <div data-guide="attention-deadlines">{body}</div>;
    case 'alert-water':
      return <div data-guide="attention-water">{body}</div>;
    default:
      return <div data-guide="attention-conflicts">{body}</div>;
  }
}

/* ----------------------------------------------- ключевые показатели */

function Indicators({ data, openTab }: { data: FarmCabinetData; openTab: (t: FarmTab) => void }) {
  const t = useT();
  const noData = t(m.noData);
  const v = (id: string) => metricNumber(data.metricById, id);
  const loading = data.metrics.state === 'loading';
  const failed = data.metrics.state === 'error';
  const show = (value: number | null, fmt: (x: number) => string) => (loading ? '…' : value === null ? noData : fmt(value));
  const harvestPlan = v('harvest.plan_t');
  // Карантин — из выдачи кабинета: те же определения, что у метрик
  // quarantine.* (сверено тестом test_cabinet_api), но без «нет данных» там,
  // где событий не было: ноль контуров в карантине — ответ, а не пробел.
  const qView = data.quarantine.data;
  const qFlagged = qView ? (qView.now.yellow ?? 0) + (qView.now.red ?? 0) : 0;
  const waterReq = v('water.requested_th_m3');
  const waterFile = v('water.delivered_m3');
  const showWaterFile = !(waterReq !== null && waterReq > 0) && waterFile !== null && waterFile > 0;
  return (
    <div data-guide="indicators">
      <Card title={t(m.kpiTitle)} aside={data.primary ? cycleName(data.primary) : null}>
        {failed ? (
          <EmptyState compact title={t(m.kpiFailTitle)} reason={data.metrics.error?.message ?? t(m.noAnswer)} />
        ) : (
          <div className={`cab-kpis cab-kpis--n${3 + (harvestPlan !== null && harvestPlan > 0 ? 1 : 0) + ((waterReq !== null && waterReq > 0) || showWaterFile ? 1 : 0)}`}>
            {/* Про землю — гектары (решение №645): главное — % площади своих
                работ и гектары после срока; счёт — мелко «по числу работ».
                Площадь не указана — карточка честно остаётся на счёте. */}
            {loading || v('area.done_pct') !== null ? (
              <KpiCard label={t(m.kpiDone)} value={show(v('area.done_pct'), oneDecimal)} unit={areaShareLabel()} note={t(m.kpiDoneNote, { done: show(v('area.done_ha'), num), work: show(v('area.work_ha'), num), share: show(v('reports.done_pct'), (x) => pct(x, 1)) })} onClick={() => openTab('cycles')} actionLabel={t(m.openCycles)} />
            ) : (
              <KpiCard label={t(m.kpiDone)} value={show(v('reports.done_pct'), (x) => pct(x, 1))} note={t(m.kpiDoneNoteCount, { noArea: noArea(), done: show(v('reports.done'), num), total: show(v('obligations.total'), num), progress: show(v('reports.in_progress'), num) })} onClick={() => openTab('cycles')} actionLabel={t(m.openCycles)} />
            )}
            {loading || v('area.overdue_ha') !== null ? (
              <KpiCard label={t(m.kpiOverdue)} value={show(v('area.overdue_ha'), num)} unit={t(m.kpiLandUnit)} tone={(v('area.overdue_ha') ?? 0) > 0 ? 'warning' : undefined} note={t(m.kpiOverdueNote, { share: show(v('area.overdue_land_pct'), (x) => pct(x, 1)), works: show(v('overdue.count'), num), ha: show(v('area.overdue_work_ha'), num) })} onClick={() => openTab('cycles')} actionLabel={t(m.openCycles)} />
            ) : (
              <KpiCard label={t(m.kpiOverdue)} value={show(v('overdue.count'), num)} unit={t(m.kpiWorksUnit)} tone={(v('overdue.count') ?? 0) > 0 ? 'warning' : undefined} note={t(m.kpiOverdueNoteCount, { noArea: noArea(), share: show(v('overdue.pct_closed'), (x) => pct(x, 1)) })} onClick={() => openTab('cycles')} actionLabel={t(m.openCycles)} />
            )}
            {harvestPlan !== null && harvestPlan > 0 ? (
              <KpiCard label={t(m.kpiHarvest)} value={show(v('harvest.plan_pct'), (x) => pct(x, 1))} note={t(m.kpiHarvestNote, { delivered: show(v('harvest.delivered_t'), num), plan: num(harvestPlan) })} onClick={() => openTab('harvest')} actionLabel={t(m.openHarvest)} />
            ) : null}
            {waterReq !== null && waterReq > 0 ? (
              <KpiCard label={t(m.kpiWater)} value={show(v('water.share_pct'), (x) => pct(x, 1))} note={t(m.kpiWaterNote, { delivered: show(v('water.delivered_th_m3'), num), requested: num(waterReq) })} onClick={() => openTab('water')} actionLabel={t(m.openWater)} />
            ) : null}
            {showWaterFile ? (
              <KpiCard label={t(m.kpiWater)} value={m3Compact(waterFile)} note={t(m.kpiWaterFileNote, { land: show(v('water.irrigated_land_ha'), num), done: show(v('water.irrigation_done_land_pct'), (x) => pct(x, 1)), noDelivery: show(v('water.no_delivery_pairs'), num) })} onClick={() => openTab('water')} actionLabel={t(m.openWater)} />
            ) : null}
            <KpiCard label={t(m.kpiQuarantine)} value={qView ? num(qFlagged) : '…'} unit={t(m.kpiContoursUnit)} tone={qFlagged > 0 ? 'warning' : undefined} note={qView ? t(m.kpiQuarantineNote, { open: num((qView.requirements.open ?? 0) + (qView.requirements.overdue ?? 0)), overdue: num(qView.requirements.overdue ?? 0) }) : t(m.kpiQuarantineLoading)} onClick={() => openTab('quarantine')} actionLabel={t(m.openQuarantine)} />
          </div>
        )}
        {harvestPlan === 0 || harvestPlan === null ? <p className="cab-why">{t(m.noHarvestPlan)}</p> : null}
      </Card>
    </div>
  );
}

function HarvestSummary({ data, farmId, reasons, openTab }: { data: FarmCabinetData; farmId: number; reasons: string[]; openTab: (t: FarmTab) => void }) {
  const t = useT();
  const noData = t(m.noData);
  const v = (id: string) => metricNumber(data.metricById, id);
  // Урожайность — не метрика района, а строка хозяйства из тех же сдач, что на вкладке «Сдача».
  const delivery = useFarmDeliveryRow(data.ctx.params, data.ctx.key, farmId, data.primary);
  const yieldCHa = harvestYieldCHa(delivery.data?.rows[0]);
  return (
    <div data-guide="harvest-summary">
      <Card title={t(m.harvestTitle)} aside={<button type="button" className="cab-signal" onClick={() => openTab('harvest')}>{t(m.harvestMore)}</button>}>
        <Why reasons={reasons} />
        <Grid cols={3}>
          <KpiCard label={t(m.harvestDelivered)} value={v('harvest.delivered_t') === null ? noData : num(v('harvest.delivered_t'))} unit={t(UNIT_LABELS.t)} note={t(m.harvestDeliveredNote, { plan: num(v('harvest.plan_t')) })} />
          <KpiCard label={t(m.harvestCompletion)} value={v('harvest.plan_pct') === null ? noData : pct(v('harvest.plan_pct'), 1)} note={t(m.harvestCompletionNote)} />
          <KpiCard label={t(m.harvestYield)} value={delivery.state === 'loading' ? '…' : yieldCHa === null ? noData : num(yieldCHa)} unit={t(m.harvestYieldUnit)} note={t(m.harvestYieldNote)} />
        </Grid>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------- земля */

/**
 * Земля, поля и агроциклы — ключевые факты хозяйства одной карточкой
 * (замечание владельца 08.10: обзор без перегруза). Пашня с орошаемой и
 * богарой — одной карточкой; агроциклы — сколько идёт и сколько завершено
 * (нажатие — вкладка «Агроциклы и работы»); ниже — культуры по годам
 * агроцикла (бывший блок «Направления»).
 */
function LandBlock({ data, openTab }: { data: FarmCabinetData; openTab: (t: FarmTab) => void }) {
  const t = useT();
  const noData = t(m.noData);
  const land = data.land.data;
  const h = land ? landHeadline(land.balance) : null;
  const contours = land ? new Set(land.contours.map((c) => c.contour_id)).size : null;
  const val = (x: number | null | undefined) => (data.land.state === 'loading' ? '…' : x === null || x === undefined ? noData : ha(x));
  const haText = (x: number | null | undefined) => (x === null || x === undefined ? noData : t(m.haValue, { ha: ha(x) }));
  const seasons = land ? directionsBySeason(land.directions).slice(0, 3) : [];
  const declared = data.actor.data?.directions ?? [];
  const active = data.active;
  const closed = data.items.filter((c) => c.status === 'closed').length;
  return (
    <div data-guide="land">
      <Card title={t(m.landTitle)} info={land?.note || undefined} aside={data.actor.data ? t(m.landAside, { contours: data.actor.data.contours }) : null}>
        {data.land.state === 'error' ? (
          <EmptyState compact title={t(m.landFailTitle)} reason={data.land.error?.message ?? t(m.noAnswer)} />
        ) : (
          <Stack>
            <Grid cols={4}>
              {isLoading(data.land) ? (
                Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} label={t(m.landLoading)} />)
              ) : (<>
              <KpiCard label={t(m.landTotal)} value={val(h?.totalHa)} unit={t(UNIT_LABELS.ha)} note={t(m.landTotalNote)} />
              <KpiCard label={t(m.landArable)} value={val(h?.arableHa)} unit={t(UNIT_LABELS.ha)} note={t(m.landArableSplit, { irrigated: haText(h?.irrigatedHa), rainfed: haText(h?.rainfedHa) })} />
              <KpiCard label={t(m.landContours)} value={contours === null ? '…' : num(contours)} note={t(m.landContoursNote)} onClick={() => openTab('contours')} actionLabel={t(m.openContours)} />
              <KpiCard
                label={t(m.landCycles)}
                value={num(data.items.length)}
                note={active.length ? t(m.landCyclesActive, { names: active.map(cycleName).join(', '), closed }) : t(m.landCyclesNone, { closed })}
                onClick={() => openTab('cycles')}
                actionLabel={t(m.openCycles)}
              />
              </>)}
            </Grid>
            <div className="cab-directions-block" data-guide="directions">
              <p className="cab-directions-block__head">
                <strong>{t(m.dirTitle)}</strong>
                {declared.length ? <span className="cab-why"> · {t(m.dirAside, { list: [...new Set(declared.map((d) => d.raw_label ?? d.direction_code))].join(', ') })}</span> : null}
                {land?.direction_note ? <InfoPopover icon="info" label={t(m.dirTitle)}>{land.direction_note}</InfoPopover> : null}
              </p>
              {isLoading(data.land) ? (
                <SkeletonList label={t(m.dirLoading)} items={2} tone="soft" />
              ) : seasons.length === 0 ? (
                <p className="cab-why">{t(m.dirNoneReason)}</p>
              ) : (
                <div className="cab-directions">
                  {seasons.map((s) => (
                    <div className="cab-directions__year" key={s.year}>
                      <span className="cab-directions__title">{t(m.dirYear, { year: s.year })}</span>
                      {s.items.map((d) => (
                        <span key={`${d.code}-${d.phase}`}>
                          <Badge tone="outline">{d.label}</Badge> {t(m.dirItem, { ha: d.ha === null ? noData : t(m.haValue, { ha: ha(d.ha) }), contours: d.contours })}
                        </span>
                      ))}
                      {s.more > 0 ? <span className="cab-why">{t(m.dirMore, { count: s.more })}</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Stack>
        )}
      </Card>
    </div>
  );
}

/* ----------------------------------------------------------- карта */

function FarmMap({ data, farmId }: { data: FarmCabinetData; farmId: number }) {
  const t = useT();
  const [layer, setLayer] = useState<ColorBy>('crop');
  const crops = useTrackData<Crop[]>(FILES.crops);
  const timeline = useAsOfTimeline();
  const rows = data.land.data?.contours ?? [];
  const ids = useMemo(() => [...new Set(rows.map((r) => String(r.contour_id)))], [rows]);
  const years = data.land.data?.cycle_years ?? [];
  const [year, setYear] = useState<number | 'registry'>('registry');
  const cycle = data.primary?.cycle ?? null;
  const status = useRequest(
    () => (layer === 'workStatus' && cycle ? api.seasonContourStatus({ ...data.ctx.params, cycle, actor_id: farmId }) : Promise.resolve(null)),
    [layer, cycle, farmId, data.ctx.key],
  );
  const quarantineById = useMemo(() => quarantineByContour(rows), [rows]);
  const waterCycle = farmWaterCycles(data).fallback;
  // Вода контуров — по событиям файла ирригации (слой считает сервер); заявки — запасная ветка.
  const water = useRequest(
    () => (layer === 'water' && waterCycle ? request<Schemas['FarmWaterOut']>(`/farms/${farmId}/water`, { ...data.ctx.params, cycle: waterCycle }) : Promise.resolve(null)),
    [layer, waterCycle, farmId, data.ctx.key],
  );
  const fileLayer = water.data?.source === 'events' ? water.data.contour_water : undefined;
  const waterById = useMemo(
    () => (fileLayer && Object.keys(fileLayer).length ? (fileLayer as Record<string, WaterStateValue>) : waterByContour(rows)),
    [fileLayer, rows],
  );
  const workStatusById = useMemo(() => (status.data ? workStatusByContour(status.data.items) : undefined), [status.data]);
  const pastLayer = useMemo(() => (year !== 'registry' ? cropSeasonLayer(rows, year) : undefined), [rows, year]);
  const effective: ColorBy = layer === 'crop' && pastLayer ? 'values' : layer;
  const unavailable =
    layer === 'water' && !waterById && water.state !== 'loading'
      ? t(m.mapNoWater)
      : layer === 'workStatus' && !status.data && status.state !== 'loading'
        ? t(m.mapNoStatus)
        : null;
  return (
    <div data-guide="map">
      <Card size="lg" title={t(m.mapTitle)} info={t(m.mapInfo)} aside={t(m.contourCount, { count: ids.length })}>
        <div className="cab-toolbar">
          {layer === 'crop' ? (
            <label>
              {t(m.mapSeason)}
              <select value={String(year)} onChange={(e) => setYear(e.target.value === 'registry' ? 'registry' : Number(e.target.value))}>
                <option value="registry">{t(m.mapRegistry)}</option>
                {years.map((y) => (
                  <option key={y} value={y}>{t(m.mapYear, { year: y })}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {/* Кадр строится, когда земля прочитана: камера подгоняется под
            контуры хозяйства при первом показе, а не под весь район. */}
        {isLoading(data.land) ? (
          <SkeletonMap label={t(m.mapLoading)} height={440} />
        ) : (
        <AgroMap
          colorBy={effective}
          layersPanel
          layers={['crop', 'workStatus', 'water', 'quarantine']}
          onColorByChange={setLayer}
          crops={crops.data ?? undefined}
          quarantineById={quarantineById}
          workStatusById={workStatusById}
          data={{ waterById, values: pastLayer }}
          layerUnavailable={unavailable}
          filterIds={ids.length ? ids : undefined}
          focusIds={ids.length ? ids : undefined}
          timeline={timeline}
          mode="frame"
          height={440}
          legend="collapsed"
          caption={layer === 'workStatus' && data.primary ? t(m.mapCaptionStatus, { cycle: cycleName(data.primary) }) : t(m.mapCaptionOnly)}
        />
        )}
      </Card>
    </div>
  );
}

/* --------------------------------------------------------- история */

function HistoryBlock({ data, reasons, openTab }: { data: FarmCabinetData; reasons: string[]; openTab: (t: FarmTab) => void }) {
  const t = useT();
  const noData = t(m.noData);
  const closed = data.items.filter((c) => c.status !== 'active' && c.status !== 'planned');
  const raised = reasons.length > 1;
  return (
    <div data-guide="history">
      <Card title={t(m.histTitle)} aside={<button type="button" className="cab-signal" onClick={() => openTab('cycles')}>{t(m.histAll)}</button>}>
        {raised ? <Why reasons={reasons.slice(1)} /> : null}
        {closed.length === 0 ? (
          <p className="cab-why">{t(m.histNone)}</p>
        ) : (
          <ul className="cab-list">
            {closed.map((c) => (
              <li className="cab-list__row" key={c.cycle}>
                <span>{cycleName(c)}</span>
                <span className="cab-list__meta">
                  {t(m.histMeta, { contours: c.contours.total, split: splitText(c.obligations), overdue: c.overdue.value === null ? noData : num(c.overdue.value) })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------- поручения */

const ASSIGNMENT_STATUS = { open: m.asgOpen, in_progress: m.asgInProgress, done: m.asgDone, checked: m.asgChecked } as const;

function AssignmentsBlock({ data, farmId }: { data: FarmCabinetData; farmId: number }) {
  const t = useT();
  const list = useRequest(() => api.assignments({ ...data.ctx.params, actor_id: farmId }).catch(() => null), [farmId, data.ctx.key]);
  const items = list.data?.items ?? [];
  return (
    <div data-guide="assignments">
      <Card title={t(m.asgTitle)} aside={list.data ? t(m.asgCount, { count: items.length }) : null}>
        {isLoading(list) ? (
          <SkeletonList label={t(m.asgLoading)} items={3} tone="soft" />
        ) : !list.data ? (
          <EmptyState compact title={t(m.asgFailTitle)} reason={t(m.asgFailReason)} />
        ) : items.length === 0 ? (
          <p className="cab-why">{t(m.asgNone)}</p>
        ) : (
          <ul className="cab-list">
            {items.slice(0, 5).map((a) => (
              <li className="cab-list__row" key={a.id}>
                <Link to={`/ops/assignments${data.ctx.linkSearch({ farm: farmId })}`}>{a.title}</Link>
                <span className="cab-list__meta">
                  {t(m.asgMeta, { status: a.status in ASSIGNMENT_STATUS ? t(ASSIGNMENT_STATUS[a.status as keyof typeof ASSIGNMENT_STATUS]) : a.status, assignee: a.assignee, due: a.due ? t(m.asgDue, { date: dateShort(a.due) }) : t(m.asgDueNone) })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
